import React, { useCallback, useEffect, useMemo, useState } from 'react';
import T from 'prop-types';
import qs from 'qs';
import cx from 'classnames';

import QuarterlyPerformance from './QuarterlyPerformance';
import Container from '../../../../components/Container';
import ErrorStateWithReload from '../../../../components/ErrorStateWithReload';
import Forecasts from '../../../app-insights/_shared/components/Forecasts';
import { useForecastSeenAt } from '../../../../hooks/useForecastSeenAt';

import { format } from '../../../../utils/date';

const emptyArray = [];

const ForecastsByCompany = ({
  settings,
  filters,
  data,
  activeForecast,
  containerGutters = 32,
  containerMaxWidth,
  preHeader,
  className,
  onFiltersChange,
}) => {
  const { pending, error } = settings;
  const { forecast, quarterly_forecasts_history } = data;

  const [activeKpi, setActiveKpi] = useState();
  const [activeQuarter, setActiveQuarter] = useState();

  const quartersOptions = useMemo(
    () =>
      activeKpi?.quarters?.map((q) => ({
        value: q,
        label: format(q, 'Q YY').replace(' ', 'Q'),
      })) || emptyArray,
    [activeKpi]
  );

  const activeSeries = useMemo(
    () => quarterly_forecasts_history?.find((s) => s?.kpi_name === activeKpi?.kpi_name),
    [quarterly_forecasts_history, activeKpi?.kpi_name]
  );

  const activeDataForecast = useMemo(
    () => forecast?.find((f) => f?.kpi_name === activeKpi?.kpi_name),
    [forecast, activeKpi?.kpi_name]
  );

  const makeScreenshotUrl = useCallback(({ view, kpi, va_kpi_id, fy_quarter, ticker }) => {
    const searchParams = qs.stringify(
      { kpi, view, va_kpi_id, fy_quarter, ticker },
      { arrayFormat: 'brackets' }
    );

    return `/kpi_forecasts/forecast_chart_screenshot.png?${searchParams}`;
  }, []);

  const handleFiltersChange = (quarter, kpi_id) => {
    onFiltersChange({
      ticker: activeForecast.ticker,
      fy_quarter: quarter,
      kpi_id,
    });
  };

  const handleQuarterChange = (quarter) => {
    if (activeQuarter === quarter) return;

    setActiveQuarter(quarter);
    handleFiltersChange(quarter, activeKpi.kpi_id);
  };

  const handleTabChange = (tab) => {
    const nextActiveKpi = activeForecast.kpis.find((item) => item.kpi_name === tab);
    const defaultQuarter = nextActiveKpi.quarters?.[0];
    const nextQuarter =
      activeQuarter && nextActiveKpi.quarters?.includes(activeQuarter)
        ? activeQuarter
        : defaultQuarter;

    setActiveKpi(nextActiveKpi);
    setActiveQuarter(nextQuarter);
    handleFiltersChange(nextQuarter, nextActiveKpi.kpi_id);
  };

  // Reset active kpi and quarter on active forecast change
  useEffect(() => {
    if (activeForecast) {
      const defaultKpi = activeForecast.kpis?.[0] || {};
      const initialKpi = activeKpi
        ? defaultKpi
        : activeForecast.kpis?.find((kpi) => kpi.kpi_id === filters.kpi_id) || defaultKpi;

      setActiveKpi(initialKpi);
      setActiveQuarter(initialKpi.quarters?.[0]);
    }
  }, [activeForecast]);

  useForecastSeenAt(
    !!activeForecast && {
      ...activeForecast,
      kpi_metric_name: activeKpi ? activeKpi?.kpi_name : activeForecast?.kpis?.[0]?.kpi_name,
    }
  );

  return (
    <div className={cx('kpi-forecasts-block__main', className)}>
      <Container
        gutters={containerGutters}
        disableGutters={containerGutters === 0}
        maxWidth={containerMaxWidth}
      >
        {error ? (
          <ErrorStateWithReload />
        ) : (
          <div className="kpi-forecasts-block__main-content">
            {preHeader}
            <Forecasts
              forecasts={forecast}
              pending={pending}
              activeTab={activeKpi?.kpi_name}
              activeQuarter={activeQuarter}
              quartersOptions={quartersOptions}
              availableKpis={activeForecast?.kpis}
              className="forecasts-page__forecasts"
              makeScreenshotUrl={makeScreenshotUrl}
              onQuarterChange={handleQuarterChange}
              onActiveForecastChange={handleTabChange}
            />
            <QuarterlyPerformance
              activeForecast={activeDataForecast}
              series={activeSeries}
              pending={pending}
              metadata={activeForecast}
            />
          </div>
        )}
      </Container>
    </div>
  );
};

ForecastsByCompany.propTypes = {
  settings: T.object.isRequired,
  filters: T.object.isRequired,
  data: T.shape({
    forecast: T.array,
    quarterly_forecasts_history: T.array,
  }).isRequired,
  activeForecast: T.shape({
    ticker: T.string,
    kpis: T.array,
    name: T.string,
    icon_url: T.string,
  }).isRequired,
  containerGutters: T.number,
  containerMaxWidth: T.number,
  preHeader: T.node,
  className: T.string,
  onFiltersChange: T.func.isRequired,
};

export default ForecastsByCompany;
